import { motion } from 'framer-motion';
import { Heart, Landmark, Factory, ShoppingBag, Zap, Truck, Briefcase, Building2, ArrowUpRight } from 'lucide-react';

const industries = [
  { name: 'Healthcare', keywords: 'Patient outcomes, compliance, interoperability, clinical workflows', icon: Heart, href: '/industries/healthcare', img: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&h=300&fit=crop' },
  { name: 'Financial Services', keywords: 'Risk modeling, fraud detection, regulatory compliance', icon: Landmark, href: '/industries/financial-services', img: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop' },
  { name: 'Manufacturing', keywords: 'Predictive maintenance, supply chain, quality automation', icon: Factory, href: '/industries/manufacturing', img: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=400&h=300&fit=crop' },
  { name: 'Retail & CPG', keywords: 'Omnichannel commerce, inventory intelligence, personalization', icon: ShoppingBag, href: '/industries/retail', img: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop' },
  { name: 'Energy & Utilities', keywords: 'Grid modernization, sustainability tracking, operational efficiency', icon: Zap, href: '/industries/energy-utilities', img: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&h=300&fit=crop' },
  { name: 'Logistics & Transport', keywords: 'Fleet optimization, route intelligence, real-time visibility', icon: Truck, href: '/industries/logistics-transport', img: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&h=300&fit=crop' },
  { name: 'Professional Services', keywords: 'Resource management, project delivery, knowledge automation', icon: Briefcase, href: '/industries/professional-services', img: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&h=300&fit=crop' },
  { name: 'Public Sector', keywords: 'Citizen services, compliance, secure infrastructure modernization', icon: Building2, href: '/industries/public-sector', img: 'https://images.unsplash.com/photo-1555848962-6e79363ec58f?w=400&h=300&fit=crop' },
];

export default function Industries() {
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="bg-[#0A0F1C] noise-subtle noise-dark py-16 lg:py-20 relative overflow-hidden"
    >
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(245,132,31,0.05)_0%,transparent_70%)]" />

      <div className="max-w-content mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="text-overline text-orange-500 mb-3">INDUSTRIES</p>
          <h2 data-testid="industries-heading" className="text-h2 text-white font-black tracking-tight mb-4">Deep Expertise. Proven Results.</h2>
          <p className="text-white/50 text-base lg:text-lg max-w-xl">Technology transforms every industry differently. We bring specialized knowledge to each sector.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {industries.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <motion.a
                key={ind.name}
                href={ind.href}
                data-testid={`industry-card-${ind.name.toLowerCase().replace(/\s+/g, '-')}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="group relative rounded-xl overflow-hidden h-48 cursor-pointer"
              >
                <img
                  src={ind.img}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Stronger gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/30 group-hover:from-black/80 group-hover:via-black/50 transition-colors duration-300" />

                <div className="relative z-10 p-5 flex flex-col justify-end h-full">
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight size={16} className="text-orange-500" />
                  </div>
                  <Icon size={20} className="text-orange-500 mb-2" />
                  <h3 className="text-white font-semibold text-lg mb-1">{ind.name}</h3>
                  <p className="text-white/70 text-xs leading-relaxed">{ind.keywords}</p>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
